import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import marketplaceReviewService, { MarketplaceReview } from '../../../services/marketplaceReviewService';
import { extractErrorMessage } from '../../../services/marketplaceService';
import StarRating from './StarRating';

interface ReviewDialogProps {
    open: boolean;
    licenseId: number;
    courseTitle: string;
    /** Vorhandene Bewertung → Bearbeiten, sonst Neu */
    existing?: MarketplaceReview | null;
    onClose: () => void;
    onSaved: (review: MarketplaceReview) => void;
}

/** Bewertungsformular auf der Lizenz-Karte: Sterne (Pflicht), Überschrift und Text optional. */
const ReviewDialog: React.FC<ReviewDialogProps> = ({ open, licenseId, courseTitle, existing, onClose, onSaved }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const [rating, setRating] = useState(0);
    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (open) {
            setRating(existing?.rating ?? 0);
            setTitle(existing?.title ?? '');
            setBody(existing?.body ?? '');
            setError(null);
        }
    }, [open, existing]);

    const submit = async () => {
        if (rating < 1) {
            setError(translate('marketplaceReviews.ratingRequired'));
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const input = { rating, title: title.trim(), body: body.trim() };
            const saved = existing
                ? await marketplaceReviewService.update(licenseId, input)
                : await marketplaceReviewService.create(licenseId, input);
            notify(translate('marketplaceReviews.saved'), { type: 'success' });
            onSaved(saved);
        } catch (err) {
            setError(extractErrorMessage(err, translate('marketplaceReviews.error')));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm" aria-labelledby="review-dialog-title">
            <DialogTitle id="review-dialog-title">{translate('marketplaceReviews.dialogTitle')}</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{courseTitle}</Typography>
                <Typography variant="body2" sx={{ color: 'var(--oa-text-2)' }}>{translate('marketplaceReviews.dialogIntro')}</Typography>
                {error && <Alert severity="error">{error}</Alert>}
                <Box>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>{translate('marketplaceReviews.ratingLabel')}</Typography>
                    <StarRating value={rating} onChange={setRating} size={28} testId="review-stars" />
                </Box>
                <TextField
                    label={translate('marketplaceReviews.titleLabel')}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    slotProps={{ htmlInput: { maxLength: 160 } }}
                    fullWidth
                />
                <TextField
                    label={translate('marketplaceReviews.bodyLabel')}
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    slotProps={{ htmlInput: { maxLength: 2000 } }}
                    multiline
                    minRows={3}
                    fullWidth
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={saving}>{translate('marketplaceReviews.cancel')}</Button>
                <Button variant="contained" onClick={submit} disabled={saving}>{translate('marketplaceReviews.submit')}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default ReviewDialog;
